import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mediaApi, ACCEPTED_EXTENSIONS } from './api';
import { keys } from '@/lib/query/keys';
import { useGate } from '@/lib/rbac/Gate';
import { useApiError } from '@/lib/query/useApiError';
import { hasErrorCode } from '@/features/shared/governance';
import { useToast } from '@/lib/ui';
import { uploadIssue, useUploadLimits } from './limits';
import { formatBytes } from '@/lib/format';
import { I18nField } from '@/lib/forms/widgets/I18nField';
import { Button } from '@/lib/ui/Button';
import { cn } from '@/lib/ui/cn';
import type { I18n } from '@/lib/api/types';

const EMPTY_ALT: I18n = { en: '', ar: '' };

/** Drag-drop upload with client pre-validation; server validates magic bytes (spec §10). */
export function UploadDropzone({ trim }: { trim?: 'transparent' } = {}) {
  const { t } = useTranslation();
  const { allowed } = useGate('media:manage');
  const qc = useQueryClient();
  const onError = useApiError();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [alt, setAlt] = useState<I18n>(EMPTY_ALT);
  const [dragOver, setDragOver] = useState(false);
  // The deployment's published caps, shared with the settings screen's capabilities query.
  const limits = useUploadLimits();

  const upload = useMutation({
    mutationFn: () => {
      // Backend requires alt.en for images; alt.ar is optional (can be added later in the
      // edit dialog). Send the pair only when an English alt is present.
      return mediaApi.upload({ file: file!, alt: alt.en.trim() ? alt : undefined, trim });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.media.all() });
      setFile(null);
      setAlt(EMPTY_ALT);
      toast.success(t('media.uploadedToast'), t('media.uploadedToastDetail'));
    },
    onError: (e) => {
      // SVG uploads are sanitized server-side; an SVG whose active content can't be removed is
      // rejected (422 `unsafe_svg`). Give a specific, actionable message (§1).
      if (hasErrorCode(e, 'unsafe_svg')) {
        toast.error(t('media.unsafeSvgTitle'), { description: t('media.unsafeSvgDetail') });
        return;
      }
      onError(e);
    },
  });

  if (!allowed) return null;

  /*
   * The pre-flight check, against the limit the SERVER will apply to this kind of file
   * (QA F-14). Checking one console-wide number was what let a 19 MB image be staged,
   * uploaded in full and then refused at 15 MB — the one failure mode a client-side size
   * check exists to prevent.
   */
  function validate(f: File): string | null {
    const issue = uploadIssue(f, limits, ACCEPTED_EXTENSIONS);
    if (!issue) return null;
    return issue.kind === 'type'
      ? t('media.unsupportedType', { ext: issue.ext })
      : t('media.exceedsLimit', { limit: formatBytes(issue.limit) });
  }

  function onPick(f: File | undefined) {
    if (!f) return;
    const err = validate(f);
    if (err) {
      toast.error(err);
      return;
    }
    setFile(f);
  }

  const isImage = file?.type.startsWith('image/');

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onPick(e.dataTransfer.files[0]);
      }}
      className={cn(
        'rounded-md border border-dashed p-lg text-center transition-colors',
        dragOver ? 'border-accent-blue bg-canvas-bone' : 'border-hairline-strong bg-canvas-bone',
      )}
    >
      {!file ? (
        <>
          <p className="text-small text-ink-mid">{t('media.dragFileHere')}</p>
          <Button variant="ghost" size="sm" className="mt-xs" onClick={() => inputRef.current?.click()}>
            {t('media.chooseFile')}
          </Button>
          {/* A sentence, so it takes the body scale: `label-sm` carries 1.1 leading
              and 0.12em tracking, which are right for a one-line slug and wrong for
              a line of prose that wraps. */}
          {trim && (
            <p className="mt-xs max-w-prose text-small text-ink-mid">{t('media.trimNotice')}</p>
          )}
          <p className="mt-xs max-w-prose text-small text-ink-mute">
            {/* Both caps, because the server applies two and one number cannot be right
                for both. They are the deployment's own, not this console's guess. */}
            {t('media.acceptedHint', {
              imageLimit: formatBytes(limits.image),
              documentLimit: formatBytes(limits.document),
            })}
          </p>
          <input
            ref={inputRef}
            type="file"
            hidden
            onChange={(e) => onPick(e.target.files?.[0])}
          />
        </>
      ) : (
        <div className="flex flex-col gap-sm text-start">
          <p className="text-small text-ink-graphite">{file.name}</p>
          {isImage && (
            <div className="flex flex-col gap-2xs">
              <I18nField
                label={t('media.altText')}
                required
                value={alt}
                onChange={setAlt}
              />
              <p className="max-w-prose text-small text-ink-mute">{t('media.altTextRequiredHint')}</p>
            </div>
          )}
          <div className="flex gap-sm">
            <Button
              loading={upload.isPending}
              disabled={isImage && !alt.en.trim()}
              onClick={() => upload.mutate(undefined)}
            >
              {t('media.upload')}
            </Button>
            <Button variant="ghost" onClick={() => setFile(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
